import React, { forwardRef } from 'react';
import { useTheme } from '@principal-ade/industry-theme';
import type { Command } from '../types/command.types';

interface CommandItemProps {
  command: Command;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

/**
 * CommandItem - Individual command item in the list
 * Displays command label, description, icon, and keyboard shortcut
 */
export const CommandItem = forwardRef<HTMLDivElement, CommandItemProps>(
  ({ command, isSelected, onClick, onMouseEnter }, ref) => {
    const { theme } = useTheme();
    const { label, description, icon, shortcut } = command;
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <div
        ref={ref}
        onClick={onClick}
        onMouseEnter={() => {
          setIsHovered(true);
          onMouseEnter();
        }}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          padding: '10px 16px',
          cursor: 'pointer',
          backgroundColor: isSelected || isHovered
            ? theme.colors.backgroundSecondary
            : 'transparent',
          borderLeft: isSelected || isHovered
            ? `3px solid ${theme.colors.primary}`
            : '3px solid transparent',
          transition: 'all 0.1s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
        onMouseDown={(e) => {
          // Prevent input blur
          e.preventDefault();
        }}
      >
        {/* Icon */}
        {icon && (
          <div
            style={{
              flexShrink: 0,
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: isSelected || isHovered ? theme.colors.primary : theme.colors.textSecondary,
            }}
          >
            {typeof icon === 'string' ? icon : icon}
          </div>
        )}

        {/* Label and Description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: isSelected || isHovered ? theme.colors.text : theme.colors.textSecondary,
              marginBottom: description ? '2px' : 0,
            }}
          >
            {label}
          </div>
          {description && (
            <div
              style={{
                fontSize: '12px',
                color: theme.colors.textMuted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {description}
            </div>
          )}
        </div>

        {/* Keyboard Shortcut */}
        {shortcut && (
          <div
            style={{
              flexShrink: 0,
              fontSize: '11px',
              color: theme.colors.textMuted,
              padding: '2px 8px',
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '4px',
              fontFamily: theme.fonts.monospace,
            }}
          >
            {shortcut}
          </div>
        )}
      </div>
    );
  }
);

CommandItem.displayName = 'CommandItem';
